import { EventName } from './events/eventsName';
import { GameEvent } from './events/gameEvent';
import { SolitaireGame } from './SolitaireGame';
import { Card } from 'components/Card';
import { Store } from './store/gameStore';
import { SoundManager } from './core/soundManager';
import { GameName } from 'constants/difficulty';
import { Container, Point, Sprite, Texture } from 'pixi.js';
import gsap from 'gsap';
import { MOVE_Z_INDEX } from 'constants/layout';
import { HintItem, HintType } from './gameHints/hintGroup';
import { CARD_HINT_MOVE_TIME } from 'constants/move';
import { Layout } from './LayoutManager';
import { CardHintDisplay } from 'components/CardHintDisplay';
import { addToLevel } from './core/Playable2DRootContainer';
import { onAssetsLoad } from './preloadGameAssets';
import { GAEvent, GAEventNames } from './analytics/events';
import { findSpiderHint } from './gameHints/SpiderHint';
import { findKlondikeHint } from './gameHints/KlondikeHints';
import { findFreeCellHint } from './gameHints/FreeCellHints';
import { PyramidHints } from './gameHints/PyramidHints';
import { shakePositions } from './helpers/shakeAnimation';
import { DebugHint } from './CustomConsole';
import { ActionType } from './quests/types';
import { GameUI } from './gameUI/GameUI';
import { Popups } from 'ui/types/popups';
import { CardglowAtlas } from './assets/generated';

export class Hints {
    game: SolitaireGame;
    lastPriorityCheck = 0;
    hintCards = [] as string[];
    glowScale = 420 / 363;
    glowTop = 32;
    borderTextureTop?: Texture;
    borderTextureMiddle?: Texture;
    borderTextureBottom?: Texture;
    borderTextureTopCut?: Texture;
    borderTextureBottomCut?: Texture;
    borderTextureMiddleCut?: Texture;
    autoNoHintPopupCount = 0;
    maxAutoNoHintPopups = Infinity;
    movesSinceLastNoHint = 0; // Counter for moves since last "no hints" popup
    noHintShownOnce = false; // Flag to track if "no hints" was shown at least once
    minMovesBeforeRepeatNoHint = 10; // Minimum moves before showing "no hints" again

    constructor(game: SolitaireGame) {
        this.game = game;
        this.hint = this.hint.bind(this);
        this.onModalClose = this.onModalClose.bind(this);
        GameEvent.add(EventName.HINT, this.hint);
        GameEvent.add(EventName.MOVE, this.resetPriorityCheck.bind(this));
        GameEvent.add(EventName.MOVE, this.checkHintsAfterMove.bind(this));
        GameEvent.add(EventName.CLOSE_MODAL, this.onModalClose);
        GameEvent.add(EventName.RESET_GAME, this.resetAutoPopupCounter.bind(this));
        onAssetsLoad(this.createBorderTexture.bind(this));
    }

    async createBorderTexture() {
        this.borderTextureTop = await CardglowAtlas.getTextureByName('glowTop.png');
        this.borderTextureBottom = await CardglowAtlas.getTextureByName('glowBottom.png');
        this.borderTextureMiddle = await CardglowAtlas.getTextureByName('glowMiddle.png');

        this.borderTextureTopCut = await CardglowAtlas.getTextureByName('glowTopCut.png');
        this.borderTextureBottomCut = await CardglowAtlas.getTextureByName('glowBottomCut.png');
        this.borderTextureMiddleCut = await CardglowAtlas.getTextureByName('glowMiddleCut.png');
    }
    resetPriorityCheck() {
        this.hintCards = [];
        this.lastPriorityCheck = 0;
    }

    incrementMoveCounter() {
        this.movesSinceLastNoHint++;
    }

    resetAutoPopupCounter() {
        this.autoNoHintPopupCount = 0;
        this.movesSinceLastNoHint = 0;
        this.noHintShownOnce = false;
    }

    checkHintsAfterMove() {
        if (Store.tutorialActive) return;
        if (!Store.gameActive) return;
        if (Store.gameOver) return;
        if (this.autoNoHintPopupCount >= this.maxAutoNoHintPopups) return;
        if (this.game.autocompleteActivated) return;

        setTimeout(() => {
            // Check again if game is still active
            if (Store.tutorialActive) return;
            if (!Store.gameActive) return;
            if (Store.gameOver) return;
            // Check again if we haven't exceeded the limit
            if (this.autoNoHintPopupCount >= this.maxAutoNoHintPopups) return;
            if (this.game.autocompleteActivated) return;
            this.autoNoHintPopupCount++;

            this.incrementMoveCounter();

            let hints = [] as HintItem[];

            switch (Store.name) {
                case GameName.KLONDIKE:
                    hints = findKlondikeHint(this.game, this.lastPriorityCheck, this.hintCards);
                    break;
                case GameName.SPIDER:
                    hints = findSpiderHint(this.game, this.lastPriorityCheck, this.hintCards);
                    break;
                case GameName.FREECELL:
                    hints = findFreeCellHint(this.game, this.lastPriorityCheck, this.hintCards);
                    break;
                case GameName.PYRAMID:
                    hints = new PyramidHints(this.game).getHint();
                    break;
            }

            // If no hints available, show appropriate popup
            if (hints.length === 0) {
                // First time: show immediately
                // Subsequent times: only show if at least 10 moves have passed
                const canShowNoHint = !this.noHintShownOnce || this.movesSinceLastNoHint >= this.minMovesBeforeRepeatNoHint;
                
                if (canShowNoHint) {
                    this.autoNoHintPopupCount++;
                    (window as any).noHintPlace = 'auto';
                    this.showNoHint();
                    this.noHintShownOnce = true;
                    this.movesSinceLastNoHint = 0; // Reset counter after showing
                }
            }
        }, 2500);
    }
    hint() {
        let hints = [] as HintItem[];

        switch (Store.name) {
            case GameName.KLONDIKE:
                hints = findKlondikeHint(this.game, this.lastPriorityCheck, this.hintCards);
                break;
            case GameName.SPIDER:
                hints = findSpiderHint(this.game, this.lastPriorityCheck, this.hintCards);
                break;
            case GameName.FREECELL:
                hints = findFreeCellHint(this.game, this.lastPriorityCheck, this.hintCards);
                break;
            case GameName.PYRAMID:
                this.game.onExternalClick();
                hints = new PyramidHints(this.game).getHint();
                break;
        }

        if (hints[0]?.type === HintType.RESET) {
            this.resetPriorityCheck();
            this.hint();
            return;
        }
        this.showConsole(hints[0]?.priority, hints[0]?.sub);

        const timeout = hints.length * CARD_HINT_MOVE_TIME * 1000;
        this.game.blockMove(timeout);
        GameEvent.emit(EventName.BLOCK_WAND_AND_HINTS, timeout);

        if (hints.length) {
            this.showHint(hints);

            setTimeout(() => {
                Store.increaseContextValue('hints').then((context) => {
                    GameEvent.emit(EventName.MISSION_ACTION, { type: ActionType.HINTS, payload: context, currentProgress: context.hints });
                });
            }, timeout);
        } else {
            (window as any).noHintPlace = 'hint';
            this.showNoHint();
        }

        if (Store.tutorialActive) return;
        Store.startGame();
        GAEvent(GAEventNames.HINT_CLICK, {
            moves: Store.movesCount,
            id: Store.gameId,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
            score: Store.score,
            timeSec: Store.time,
            timeMin: Store.timeMin,
        });
    }
    showConsole(checkPoint: number, sub: number) {
        DebugHint('Check Point: ' + checkPoint + ' : ' + sub);
    }
    showNoHint() {
        if (Store.tutorialActive) return;
        SoundManager.play('noHintSFX');

        if (this.game.gameStrategy) {
            this.game.gameStrategy.noHintHandler();
            return;
        }

        if (Store.wandCounter === 0) {
            GameUI.showPopup(Popups.OUT_MOVE_ADS_POPUP);
            GAEvent(GAEventNames.OUT_MOVE_AD_POPUP, {
                id: Store.gameId,
                type: Store.getGameTypeText(),
                difficulty: Store.getDifficultyText(),
                trigger: (window as any).noHintPlace,
            });
        } else {
            GameUI.showPopup(Popups.OUT_MOVE_WAND_POPUP);
            GAEvent(GAEventNames.OUT_MOVE_WAND_POPUP, {
                id: Store.gameId,
                type: Store.getGameTypeText(),
                difficulty: Store.getDifficultyText(),
                trigger: (window as any).noHintPlace,
            });
        }

        Store.pauseGame();
    }
    onModalClose({ id, confirm }: { id: string; confirm: boolean }) {
        if (!confirm && ['out-move-ads', 'out-move-wand'].includes(id)) {
            this.showNoHintModal();
        }
    }

    showNoHintModal() {
        GameUI.showPopup(Popups.NO_HINTS_POPUP);
        GAEvent(GAEventNames.SHOW_NO_HINT_MODAL, {
            moves: Store.movesCount,
            id: Store.gameId,
            type: Store.getGameTypeText(),
            difficulty: Store.getDifficultyText(),
            trigger: (window as any).noHintPlace,
        });
    }
    showHint(hintItems: HintItem[]) {
        if (!Store.tutorialActive) {
            if (this.lastPriorityCheck > hintItems[0].priority)
                this.hintCards = [...hintItems[0].moveGroup].map((card) => card._id);
            else this.hintCards = [...this.hintCards, ...hintItems[0].moveGroup.map((card) => card._id)];

            Store.setHintCounter(Store.hintCounter - 1);
            Store.hints++;
            SoundManager.play('hintSFX');
        }
        if (this.lastPriorityCheck != hintItems[0].priority) this.lastPriorityCheck = hintItems[0].priority;
        

        GameEvent.emit(EventName.HINT_SHOW);

        const hintDuration = CARD_HINT_MOVE_TIME;
        hintItems.forEach((hintItem, groupIndex) => {
            let { moveGroup, to, type } = hintItem;
            let zIndex = MOVE_Z_INDEX - groupIndex;
            if (type === HintType.MOVE_STOCKPILE) {
                moveGroup = [moveGroup[0]];
                this.lastPriorityCheck++; //do not allow to check this condition again
            }

            if (type === HintType.RECYCLE) {
                moveGroup = [];
                zIndex = -1;
                this.lastPriorityCheck++; //do not allow to check this condition again
            }
            let moveTo = moveGroup.length
                ? (moveGroup[0].container.position as Point)
                : (to.container.position as Point);
            if (type === HintType.DEAL_SPIDER) {
                const lastStockPile = this.game.stockPile.lastCard;
                if (lastStockPile) {
                    moveTo = to.getNextCardPosition();
                }
            }

            let hintContainer = null;

            if (type === HintType.PYRAMID_KING) {
                hintContainer = this.createHintGroup(moveGroup, type);
                hintContainer.position = moveTo;
                hintContainer.zIndex = zIndex;
                this.hintAlfaAnimation(hintContainer, hintDuration, hintItems.length);
                return;
            }

            if (type === HintType.PYRAMID_MOVE && moveGroup.length > 1) {
                const container1 = this.createHintGroup([moveGroup[0]], type);
                const container2 = this.createHintGroup([moveGroup[1]], type);

                container1.position = moveTo;
                container1.zIndex = zIndex;

                container2.position = moveGroup[1].container.position;
                container2.zIndex = zIndex + 1;

                gsap.to(container2, {
                    x: container1.x,
                    y: container1.y,
                    duration: CARD_HINT_MOVE_TIME,
                    ease: 'power1.inOut',
                }).then(() => {
                    container1.zIndex -= 2;
                    container2.zIndex -= 2;
                });
                this.hintAlfaAnimation(container1, hintDuration, hintItems.length);
                this.hintAlfaAnimation(container2, hintDuration, hintItems.length);
                return;
            }

            hintContainer = this.createHintGroup(moveGroup, type);
            hintContainer.position = moveTo;
            hintContainer.zIndex = zIndex;

            const targetPosition = to.getNextCardPosition();
            if (type !== HintType.DEAL_SPIDER) {
                gsap.to(hintContainer, {
                    x: targetPosition.x,
                    y: targetPosition.y + Layout.getCardGap().normal * groupIndex,
                    duration: hintDuration,
                    ease: 'power1.inOut',
                    delay: groupIndex * hintDuration,
                }).then(() => {
                    hintContainer.zIndex = zIndex - 2;
                });
            } else {
                this.shakeStockPile(hintContainer);
            }

            this.hintAlfaAnimation(hintContainer, hintDuration, hintItems.length);
        });
    }


    hintAlfaAnimation(hintContainer: Container, hintDuration: number, length: number) {
        gsap.to(hintContainer, {
            alpha: 0,
            duration: hintDuration / 1.75,
            delay: length * hintDuration,
        }).then(() => {
            hintContainer.destroy({ children: true });
        });
    }
    shakeStockPile(hintContainer: Container) {
        const { x, y } = hintContainer.position;
        shakePositions.forEach((position, index) => {
            gsap.to(hintContainer, {
                x: x + position.x * 2,
                y: y + position.y * 2,
                duration: 0.1,
                delay: index * 0.1,
            });
        });
    }
    createHintGroup(moveGroup: Card[], type: HintType, hideCards: boolean = false): Container {
        const moveContainer = new Container();
        const { width, height } = Layout.getCardSize();
        let groupHeight = 0;
        const startPosition = moveGroup.length ? moveGroup[0].position : new Point(0, 0);
        const glowContainer = new Container();
        moveContainer.addChild(glowContainer);
        if (!hideCards) {
            moveGroup.forEach((card) => {
                const hintCard = new CardHintDisplay(card);
                groupHeight = Math.abs(startPosition.y - card.position.y);
                hintCard.container.position.y = card.position.y - startPosition.y;
                moveContainer.addChild(hintCard.container);
            });
        }
        const coeffW = type === HintType.RECYCLE ? 1.1 : 1;
        const coeffH = type === HintType.RECYCLE ? 1.03 : 1;
        const glowWidth = width * coeffW * this.glowScale;
        const topSprite = new Sprite(type === HintType.RECYCLE ? this.borderTextureTopCut : this.borderTextureTop);
        glowContainer.addChild(topSprite);
        topSprite.anchor.set(0.5, 0);
        topSprite.width = glowWidth;
        topSprite.position.set(0, (-height / 2 - this.glowTop * topSprite.scale.x) * coeffH);
        topSprite.scale.y = topSprite.scale.x;

        const bottomSprite = new Sprite(
            type === HintType.RECYCLE ? this.borderTextureBottomCut : this.borderTextureBottom
        );
        glowContainer.addChild(bottomSprite);
        bottomSprite.anchor.set(0.5, 1);
        bottomSprite.width = glowWidth;
        bottomSprite.position.set(0, (height / 2 + this.glowTop * bottomSprite.scale.x + groupHeight) * coeffH);
        bottomSprite.scale.y = bottomSprite.scale.x;

        const middleSprite = new Sprite(
            type === HintType.RECYCLE ? this.borderTextureMiddleCut : this.borderTextureMiddle
        );
        glowContainer.addChild(middleSprite);
        middleSprite.anchor.set(0.5);
        middleSprite.width = glowWidth;
        middleSprite.position.set(0, groupHeight / 2);
        middleSprite.height = groupHeight + height - height * 0.26;

        addToLevel(moveContainer);
        return moveContainer;
    }
}
